import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { ShieldCheck, Award, TrendingUp, Loader2 } from 'lucide-react';
import { TrustBadge } from './TrustBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LAYER_META = [
  { key: 'l1_identity',   title: 'Identity',   hint: 'Verify email, phone, address, and government ID',   max: 25 },
  { key: 'l2_business',   title: 'Business',   hint: 'Verify company registration, website, and business age', max: 25 },
  { key: 'l3_reputation', title: 'Reputation', hint: 'Collect reviews from real buyers (5+ reviews for full score)', max: 25 },
  { key: 'l4_platform',   title: 'Platform',   hint: 'Respond quickly to messages, keep listings quality high', max: 25 },
];

const NEXT_STEP_HINTS = (breakdown, sellerType) => {
  const hints = [];
  if ((breakdown?.l1_identity || 0) < 15) hints.push('Verify your phone + upload a government ID (~+17 pts)');
  if (sellerType === 'business' && (breakdown?.l2_business || 0) < 15) hints.push('Verify your Companies House registration (~+10 pts)');
  if ((breakdown?.l3_reputation || 0) < 10) hints.push('Get your first 5 buyer reviews (~+15 pts)');
  if ((breakdown?.l4_platform || 0) < 15) hints.push('Reply to buyer messages within 1 hour (~+5 pts)');
  return hints;
};

export const TrustScoreCard = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchScore = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/trust/score/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load trust score');
    } finally {
      setLoading(false);
    }
  };

  const recompute = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/trust/score/recompute`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      setError('Unable to recompute');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScore(); }, []);

  if (loading) {
    return (
      <Card data-testid="trust-score-card-loading">
        <CardContent className="p-6 flex items-center gap-2 text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading trust score…
        </CardContent>
      </Card>
    );
  }
  if (error || !data) {
    return null; // silently hide if unauthorized
  }

  const total = data.total || 0;
  const tier = data.tier || 'UNVERIFIED';
  const hints = NEXT_STEP_HINTS(data, data.seller_type);

  return (
    <Card data-testid="trust-score-card" className="border-2">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Trust Intelligence™
          </CardTitle>
          <TrustBadge tier={tier} score={total} size="md" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-end justify-between mb-1">
            <span className="text-3xl font-bold text-gray-900" data-testid="trust-score-total">{Math.round(total)}</span>
            <span className="text-sm text-gray-500">/ 100</span>
          </div>
          <Progress value={total} className="h-2" />
          <p className="text-xs text-gray-500 mt-1">
            {tier === 'GOLD' && 'Highest trust tier - buyers see your Gold badge.'}
            {tier === 'VERIFIED' && 'Great start! Reach 80 to earn the Gold badge.'}
            {tier === 'UNVERIFIED' && 'Reach 60 points to unlock the Verified badge.'}
            {tier === 'BLOCKED' && 'Your account is under review. Contact support.'}
          </p>
        </div>

        {!compact && (
          <div className="space-y-2 pt-2 border-t">
            {LAYER_META.map((l) => {
              const val = data[l.key] || 0;
              const pct = (val / l.max) * 100;
              return (
                <div key={l.key} data-testid={`trust-layer-${l.key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{l.title}</span>
                    <span className="text-gray-600">{val.toFixed(1)} / {l.max}</span>
                  </div>
                  <Progress value={pct} className="h-1.5 mt-1" />
                </div>
              );
            })}
          </div>
        )}

        {hints.length > 0 && !compact && (
          <div className="pt-3 border-t space-y-1">
            <p className="text-sm font-semibold flex items-center gap-1 text-emerald-700">
              <TrendingUp className="h-4 w-4" /> Improve your score
            </p>
            <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
              {hints.slice(0, 3).map((h, i) => (
                <li key={i} data-testid={`trust-hint-${i}`}>{h}</li>
              ))}
            </ul>
          </div>
        )}

        {!compact && (
          <div className="flex gap-2">
            <Button asChild size="sm" className="flex-1" data-testid="trust-get-verified-btn">
              <Link to="/get-verified">
                <ShieldCheck className="h-4 w-4 mr-1" /> Get verified
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={recompute} data-testid="trust-recompute-btn">
              Refresh
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TrustScoreCard;
